import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi/useApi.ts";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useNotification } from "~hooks/useNotification.ts";
import { useUpdatePermissionScopeCache } from "~hooks/useUpdatePermissionScopeCache.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";

const useDeleteEntityGroup = () => {
  const { sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const request = useApi();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation<DefaultOperationSuccessResponse, Error, string>({
    mutationFn: async (entityGroupId: string) => {
      return await request(`${sport}/o/${organizationId}/entityGroups/${entityGroupId}`, { method: "DELETE" });
    },
    onSuccess: async (response: DefaultOperationSuccessResponse, entityGroupId) => {
      const operationResult = response?.data?.[0];

      if (!operationResult?.success) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        updatedScope.scope.entityGroupTree = updatedScope.scope.entityGroupTree.filter(({ entityGroupId: id }) => id !== entityGroupId);

        if (updatedScope.resources?.entityGroups?.[entityGroupId]) {
          delete updatedScope.resources.entityGroups[entityGroupId];
        }
      });

      notification.success({
        message: formatMessage({ id: "entityGroup.delete.success" }),
        placement: "bottomRight",
      });

      await Promise.all([invalidateCache(), queryClient.invalidateQueries({ queryKey: ["teamGroups", organizationId] })]);
    },
  });
};

export default useDeleteEntityGroup;
